import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit"
import { Location } from "gen/company/models_pb"
import {
  Contacts_Contact as CustomerContact,
  PurchaseOrder,
} from "gen/customer/models_pb"
import { Timestamp } from "gen/google/protobuf/timestamp_pb"
import { Address } from "gen/proto/address/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import { Decimal } from "shared-ts/classes/decimal"
import { Money } from "shared-ts/classes/money"
import { UUID } from "shared-ts/classes/uuid"

import { AppState } from "app/store"

import { Fulfillment, StrictFulfillment } from "./FulfillmentLocation"
import { RestockFee } from "./salesSlice"
import { SaleProduct } from "./types"
import { RestockFeeType, RestockFeeTypes } from "./types/SaleForm.types"

export interface ReturnForm {
  quantityToReturnBySaleProductId: Record<string, Decimal>
  returnNotes: string
  address: Address | null
  po: PurchaseOrder | null
  authorizedBuyer: CustomerContact | null
  restockFeeAmount: Money | null
  restockFeePercentage: Decimal | null
  restockFeeType: RestockFeeType
  fulfillment: Fulfillment
}

export interface ReturnFormState {
  initialState: ReturnForm
  currentState: ReturnForm
}

export const emptyReturnForm: ReturnForm = {
  quantityToReturnBySaleProductId: {},
  returnNotes: "",
  address: null,
  po: null,
  authorizedBuyer: null,
  restockFeeAmount: null,
  restockFeePercentage: null,
  restockFeeType: RestockFeeTypes.PERCENTAGE, // default fee type
  fulfillment: {
    location: null,
    isDelivery: false,
    deliveryDate: null,
  },
}

const initialState: ReturnFormState = {
  initialState: emptyReturnForm,
  currentState: emptyReturnForm,
}

export const returnsSlice = createSlice({
  name: "returns",
  initialState,
  reducers: {
    setInitialReturnNotes: (state, action: PayloadAction<string>) => {
      state.initialState.returnNotes = action.payload
      state.currentState.returnNotes = action.payload
    },
    setInitialState: (
      state,
      action: PayloadAction<{
        saleProducts?: SaleProduct[]
        returnNotes?: string
        address?: Address | undefined
        po?: PurchaseOrder | undefined
        fulfillmentLocation?: Location | undefined
        isDeliveryFulfillment?: boolean
        deliveryDate?: Timestamp | undefined
      }>
    ) => {
      const returnProductsById: Record<string, Decimal> = {}
      for (const saleProduct of action.payload.saleProducts ?? []) {
        const idStr = UUID.idString(saleProduct.id)
        if (!idStr) {
          continue
        }
        returnProductsById[idStr] = Decimal.zero()
      }

      const initialState: ReturnForm = {
        quantityToReturnBySaleProductId: returnProductsById,
        returnNotes: action.payload.returnNotes ?? "",
        address: action.payload.address ?? null,
        po: action.payload.po ?? null,
        authorizedBuyer: null,
        restockFeeAmount: null,
        restockFeePercentage: null,
        restockFeeType: RestockFeeTypes.PERCENTAGE,
        fulfillment: {
          location: action.payload.fulfillmentLocation ?? null,
          isDelivery: action.payload.isDeliveryFulfillment ?? false,
          deliveryDate: action.payload.deliveryDate
            ? Timestamp.toDate(action.payload.deliveryDate)
            : null,
        },
      }
      state.initialState = initialState
      state.currentState = initialState
    },
    resetFormToCurrent: (state) => {
      state.initialState = state.currentState
    },
    setCurrentStateToInitial: (state) => {
      state.currentState = state.initialState
    },
    setReturnNotes: (state, action: PayloadAction<string>) => {
      state.currentState.returnNotes = action.payload
    },
    setAddress: (state, action: PayloadAction<Address | null>) => {
      state.currentState.address = action.payload
    },
    setPo: (state, action: PayloadAction<PurchaseOrder | null>) => {
      state.currentState.po = action.payload
    },
    setAuthorizedBuyer: (
      state,
      action: PayloadAction<CustomerContact | null>
    ) => {
      state.currentState.authorizedBuyer = action.payload
    },
    setQuantityToReturn: (
      state,
      action: PayloadAction<{ saleProductId?: PBUUID; quantity: Decimal }>
    ) => {
      const idString = UUID.idString(action.payload.saleProductId)
      if (!idString) {
        return
      }

      if (!(idString in state.currentState.quantityToReturnBySaleProductId)) {
        return
      }

      state.currentState.quantityToReturnBySaleProductId[idString] =
        action.payload.quantity
    },
    setRestockFeeAmount: (state, action: PayloadAction<Money | null>) => {
      state.currentState.restockFeeAmount = action.payload
    },
    setRestockFeePercentage: (state, action: PayloadAction<Decimal | null>) => {
      state.currentState.restockFeePercentage = action.payload
    },
    setRestockFeeType: (state, action: PayloadAction<RestockFeeType>) => {
      state.currentState.restockFeeType = action.payload
    },
    setFulfillmentLocation: (
      state,
      action: PayloadAction<{
        fulfillment: StrictFulfillment
      }>
    ) => {
      state.currentState.fulfillment = action.payload.fulfillment
    },
  },
})

export const {
  setInitialReturnNotes,
  setInitialState,
  resetFormToCurrent,
  setAddress,
  setPo,
  setAuthorizedBuyer,
  setQuantityToReturn,
  setReturnNotes,
  setRestockFeeAmount,
  setRestockFeePercentage,
  setRestockFeeType,
  setFulfillmentLocation,
} = returnsSlice.actions

export const selectQuantityToReturnBySaleProductId = (state: AppState) =>
  state.returns.currentState.quantityToReturnBySaleProductId
export const selectReturnNotes = (state: AppState) =>
  state.returns.currentState.returnNotes
export const selectAddress = (state: AppState) =>
  state.returns.currentState.address
export const selectFulfillment = (state: AppState) =>
  state.returns.currentState.fulfillment

export const selectPo = (state: AppState) => state.returns.currentState.po

export const selectAuthorizedBuyer = (state: AppState) =>
  state.returns.currentState.authorizedBuyer

export const selectIsValidForm = (state: AppState, allowEmptyNotes = false) => {
  const returnNotes = selectReturnNotes(state)
  return (
    (allowEmptyNotes || !!returnNotes) &&
    Object.values(
      state.returns.currentState.quantityToReturnBySaleProductId
    ).some((quantity) => quantity.gt(Decimal.zero()))
  )
}

export function getQuantityToReturn(
  quantityToReturnBySaleProductId: Record<string, Decimal>,
  saleProductId?: PBUUID
) {
  const idString = UUID.idString(saleProductId)
  if (!idString) {
    return Decimal.zero()
  }
  if (!(idString in quantityToReturnBySaleProductId)) {
    return Decimal.zero()
  }
  return quantityToReturnBySaleProductId[idString]
}

export const selectRestockFeeAmount = (state: AppState) =>
  state.returns.currentState.restockFeeAmount

export const selectRestockFeePercentage = (state: AppState) =>
  state.returns.currentState.restockFeePercentage

export const selectRestockFeeType = (state: AppState) =>
  state.returns.currentState.restockFeeType

export const selectRestockFee = createSelector(
  [
    (state: AppState) => state.returns.currentState.restockFeeType,
    (state: AppState) => state.returns.currentState.restockFeePercentage,
    (state: AppState) => state.returns.currentState.restockFeeAmount,
  ],
  (type, percentage, amount): RestockFee => {
    if (!type) return null
    if (type === RestockFeeTypes.PERCENTAGE && percentage != null) {
      return { type: "rate", rate: percentage }
    }
    if (type === RestockFeeTypes.FIXED && amount != null) {
      return { type: "fixed", amount: amount }
    }
    return null
  }
)
